import { Card, ConfirmationModal, Space } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { ReportAccessGrantListPresenter } from '@webapp/platform/iam/features/list-report-access-grants/presenters/ReportAccessGrantListPresenter'
import { ReportAccessSecondaryCategoryListPresenter } from '@webapp/platform/iam/features/list-report-access-secondary-categories/presenters/ReportAccessSecondaryCategoryListPresenter'
import { ReportAccessActionRow } from './components/ReportAccessActionRow'
import { ReportAccessDetailHeader } from './components/ReportAccessDetailHeader'
import { ReportAccessHeaderCard } from './components/ReportAccessHeaderCard'
import messages from './messages'
import { useReportAccessView } from './useReportAccessView'

/**
 * Feature Set: Report Access View (Page-level)
 *
 * Single master/detail surface orchestrating nine features: the
 * PrimaryCategory bootstrap + selector, the SecondaryCategory master list,
 * the grants tag list, and six grant/revoke commands dispatched from one
 * picker by mode. Owns all mode state and both danger confirmations.
 */
export const ReportAccessView = () => {
  const intl = useProcurifyIntl()

  const {
    states: {
      selectedPrimaryCategory,
      isAllPrimaryCategories,
      selectedSecondaryCategory,
      isAllSecondaryCategories,
      submitAction,
      highlightedGrantId,
      isClearAllConfirmOpen,
      isBulkRemoveConfirmOpen,
      pendingClearAllGrants,
      pendingBulkRemoveUser,
      isSubmitting,
    },
    commands: {
      onSubmitUser,
      onRemoveGrant,
      onClearAllConfirm,
      onBulkRemoveConfirm,
    },
    methods: {
      onSelectPrimaryCategory,
      onSelectAllPrimaryCategories,
      onSelectSecondaryCategory,
      onToggleSelectAllSecondaryCategories,
      onSubmitActionChange,
      onClearAllRequest,
      onClearAllDismiss,
      onBulkRemoveDismiss,
    },
  } = useReportAccessView()

  const isSingleSecondaryCategoryMode =
    !isAllPrimaryCategories && !isAllSecondaryCategories

  return (
    <>
      <Space size='large'>
        <ReportAccessHeaderCard
          selectedPrimaryCategory={selectedPrimaryCategory}
          isAllPrimaryCategoriesSelected={isAllPrimaryCategories}
          onSelectPrimaryCategory={onSelectPrimaryCategory}
          onSelectAllPrimaryCategories={onSelectAllPrimaryCategories}
        />

        <Space direction='horizontal' align='start'>
          {/* Left pane unmounts entirely in ALL-PrimaryCategories mode
              (BHV-005) — the branch-less Angular fetch is intentionally
              skipped */}
          {!isAllPrimaryCategories && selectedPrimaryCategory && (
            <Space.Item width={1 / 3}>
              <Card>
                <Card.Content>
                  <ReportAccessSecondaryCategoryListPresenter
                    primaryCategoryId={selectedPrimaryCategory.id}
                    primaryCategoryName={selectedPrimaryCategory.name}
                    selectedSecondaryCategoryId={selectedSecondaryCategory?.id}
                    isAllSecondaryCategoriesSelected={isAllSecondaryCategories}
                    onSelect={onSelectSecondaryCategory}
                    onToggleSelectAll={onToggleSelectAllSecondaryCategories}
                  />
                </Card.Content>
              </Card>
            </Space.Item>
          )}

          <Space.Item width={isAllPrimaryCategories ? 1 : 2 / 3}>
            <Card>
              <Card.Content>
                <Space>
                  <ReportAccessDetailHeader
                    isAllPrimaryCategories={isAllPrimaryCategories}
                    isAllSecondaryCategories={isAllSecondaryCategories}
                    secondaryCategoryName={selectedSecondaryCategory?.name}
                    primaryCategoryName={selectedPrimaryCategory?.name}
                  />

                  <ReportAccessActionRow
                    isAllPrimaryCategoriesMode={isAllPrimaryCategories}
                    submitAction={submitAction}
                    onSubmitActionChange={onSubmitActionChange}
                    onSubmit={onSubmitUser}
                    isSubmitting={isSubmitting}
                  />

                  {/* Grants list renders in single-SecondaryCategory mode
                      only (BHV-011) */}
                  {isSingleSecondaryCategoryMode &&
                    selectedSecondaryCategory && (
                      <ReportAccessGrantListPresenter
                        secondaryCategoryId={selectedSecondaryCategory.id}
                        highlightedGrantId={highlightedGrantId}
                        onRemoveGrant={onRemoveGrant}
                        onClearAll={onClearAllRequest}
                      />
                    )}
                </Space>
              </Card.Content>
            </Card>
          </Space.Item>
        </Space>
      </Space>

      {/* FS-owned danger confirmation: clear all grants on one
          SecondaryCategory (BHV-019) */}
      <ConfirmationModal
        open={isClearAllConfirmOpen}
        type='danger'
        header={intl.formatMessage(messages.clearAllConfirmHeader)}
        content={intl.formatMessage(messages.clearAllConfirmMessage, {
          count: pendingClearAllGrants.length,
          secondaryCategoryName: selectedSecondaryCategory?.name,
        })}
        confirmButtonText={intl.formatMessage(messages.confirmButtonText)}
        cancelButtonText={intl.formatMessage(messages.cancelButtonText)}
        onConfirm={onClearAllConfirm}
        handleDismiss={onClearAllDismiss}
      />

      {/* FS-owned danger confirmation: bulk remove from all
          PrimaryCategories (BHV-025) */}
      <ConfirmationModal
        open={isBulkRemoveConfirmOpen}
        type='danger'
        header={intl.formatMessage(messages.bulkRemoveConfirmHeader)}
        content={intl.formatMessage(messages.bulkRemoveConfirmMessage, {
          userFullName: pendingBulkRemoveUser?.fullName,
        })}
        confirmButtonText={intl.formatMessage(messages.confirmButtonText)}
        cancelButtonText={intl.formatMessage(messages.cancelButtonText)}
        onConfirm={onBulkRemoveConfirm}
        handleDismiss={onBulkRemoveDismiss}
      />
    </>
  )
}

ReportAccessView.displayName = 'ReportAccessView'
